import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const LoginPage = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [logo, setLogo] = useState(null);
    const { login, API } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const response = await axios.get(`${API}/settings/logo`);
                if (response.data.logo) setLogo(response.data.logo);
            } catch (error) { /* ignore */ }
        };
        fetchLogo();
    }, [API]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username || !password) {
            toast.error('Please enter username and password');
            return;
        }
        setLoading(true);
        try {
            await login(username.trim(), password);
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4"
            style={{ background: 'linear-gradient(160deg, #1e3a5f 0%, #152a47 50%, #1a3355 100%)' }}
            data-testid="login-page"
        >
            <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl px-8 py-10">
                {/* Logo */}
                <div className="flex justify-center mb-6">
                    {logo ? (
                        <img
                            src={logo}
                            alt="School Logo"
                            className="h-12 object-contain"
                            data-testid="login-logo"
                        />
                    ) : (
                        <span className="text-ica-navy font-semibold text-lg tracking-wide">ICA</span>
                    )}
                </div>

                {/* Title */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Pass!</h1>
                    <p className="text-gray-400 text-sm mt-1">The ICA Attendance System</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-1.5">
                        <Label htmlFor="username" className="text-sm text-gray-600">
                            Username
                        </Label>
                        <Input
                            id="username"
                            data-testid="username-input"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder="Enter your username"
                            className="h-12 bg-gray-50 border-gray-200 rounded-lg focus:border-ica-gold focus:ring-ica-gold/20 text-sm"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="password" className="text-sm text-gray-600">
                            Password
                        </Label>
                        <div className="relative">
                            <Input
                                id="password"
                                data-testid="password-input"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                className="h-12 bg-gray-50 border-gray-200 rounded-lg pr-11 focus:border-ica-gold focus:ring-ica-gold/20 text-sm"
                            />
                            <button
                                type="button"
                                data-testid="toggle-password-btn"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>

                    <div className="pt-2">
                        <Button
                            type="submit"
                            data-testid="login-submit-btn"
                            disabled={loading}
                            className="w-full h-12 rounded-full bg-ica-gold hover:bg-[#a8803a] text-white font-semibold text-sm tracking-wide transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                'Login'
                            )}
                        </Button>
                    </div>
                </form>

                <p className="mt-8 text-center text-xs text-gray-400">
                    Need help? Contact your administrator
                </p>
            </div>
        </div>
    );
};

export default LoginPage;
